import { useState } from 'react'
import type { SortCallbackParams } from '@vtex/admin-ui'
import { ColumnsIDs } from '@/components/products-listing/hooks/useProductListTableColumns'
import { useAnalytics } from '@/contexts/analytics'
import { transformSortPaginationToEventProperties } from '@/contexts/analytics/lib/transformProductListFilters'
import type { Product } from '@/types/product'

export enum Order {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum OrderBy {
  ID = 'ID',
  EditedDate = 'Date',
}

export type PaginationOrder = {
  order: Order
  orderBy: OrderBy
}

type NextPage = {
  after: string
  afterDate?: string
}

type PrevPage = {
  before: string
  beforeDate?: string
}

type SortPagination = PaginationOrder & Partial<NextPage | PrevPage>

export const useSortPagination = (columnId: string) => {
  const [sortPagination, setSortPagination] = useState<SortPagination>({
    order: Order.DESC,
    orderBy: OrderBy.EditedDate,
  })
  const analytics = useAnalytics()

  const handlePrevPage = (firstProduct?: Product) => {
    if (!firstProduct) return

    setSortPagination((current) => ({
      order: current.order,
      orderBy: current.orderBy,
      beforeDate: firstProduct.lastUpdateDate,
      before: firstProduct.id,
    }))
  }

  const handleNextPage = (lastProduct?: Product) => {
    if (!lastProduct) return

    setSortPagination((current) => ({
      order: current.order,
      orderBy: current.orderBy,
      afterDate: lastProduct.lastUpdateDate,
      after: lastProduct.id,
    }))
  }

  const toggleOrder = ({
    currentSortState: { order = Order.DESC, by: orderBy = OrderBy.ID },
    dispatch,
  }: SortCallbackParams<Product>) => {
    const newOrder = order === Order.DESC ? Order.ASC : Order.DESC

    setSortPagination((current) => ({
      order: newOrder,
      orderBy: current.orderBy,
    }))
    dispatch({ type: newOrder, columnId })
    analytics.track('Clicked on the list', {
      Action: 'Sort listing',
      'Sort by': transformSortPaginationToEventProperties({
        order: order === 'DESC' ? Order.DESC : Order.ASC,
        orderBy:
          orderBy === ColumnsIDs.LAST_UPDATE ? OrderBy.EditedDate : OrderBy.ID,
      }),
    })
  }

  return {
    handlePrevPage,
    handleNextPage,
    toggleOrder,
    initialSortValue: { by: columnId, order: Order.DESC },
    sortPagination: new URLSearchParams(sortPagination).toString(),
  }
}
